"use client";

import React, { useRef, useState, useEffect, useMemo } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import {
  faHandshake,
  faSearch,
  faCogs,
  faFileAlt,
  faLaptopCode,
  faVial,
  faGraduationCap,
  faLifeRing,
} from "@fortawesome/free-solid-svg-icons";
import { useInView } from "react-intersection-observer";
import { useSpring, animated } from "@react-spring/web";
import PrivacyConfirmModal from "./PrivacyConfirmModal";

interface Step {
  title: string;
  description: string;
  icon: any; // FontAwesome icon type
}

interface ProcessStepProps {
  step: Step;
  index: number;
  totalSteps: number;
  inView: boolean;
  isDark: boolean;
  isLast: boolean;
}

const ProcessStep: React.FC<ProcessStepProps> = ({ step, index, totalSteps, inView, isDark, isLast }) => {
  const animation = useSpring({
    from: { opacity: 0, transform: 'translateY(30px)' },
    to: { 
      opacity: inView ? 1 : 0, 
      transform: inView ? 'translateY(0px)' : 'translateY(30px)' 
    },
    delay: index * 200,
    config: { tension: 120, friction: 14 },
  });

  return (
    <animated.article 
      style={animation} 
      className="relative flex items-start group"
      aria-label={`Process step ${index + 1} of ${totalSteps}: ${step.title}`}
    >
      {/* Timeline line */}
      {!isLast && (
        <div className={`
          absolute left-6 top-14 w-0.5 h-24 md:h-32
          ${isDark ? 'bg-gradient-to-b from-green-500 to-transparent' : 'bg-gradient-to-b from-green-900 to-transparent'}
          transform transition-all duration-500
          group-hover:h-20 md:group-hover:h-28
        `} />
      )}

      {/* Step content */}
      <div className={`
        relative
        flex-1
        p-6
        pl-8
        rounded-2xl
        border-2
        transition-all
        duration-300
        hover:-translate-y-1
        ${isDark 
          ? "bg-black/80 border-green-500/30 hover:border-green-400" 
          : "bg-white/80 border-green-900/30 hover:border-green-800"
        }
      `}>
        {/* Icon */}
        <div className={`
          absolute -left-4
          w-12 h-12
          rounded-xl
          flex items-center justify-center
          transition-all duration-300
          group-hover:scale-110
            ${isDark 
            ? "bg-green-500/10 border border-green-500/30" 
            : "bg-green-900/10 border border-green-900/30"
          }
        `}>
            <FontAwesomeIcon 
              icon={step.icon} 
            className={`w-6 h-6 ${
              isDark ? "text-green-400" : "text-green-900"
            }`} 
            />
          </div>

        {/* Step number */}
        <div className={`
          absolute -left-6 -top-3
          w-6 h-6
          rounded-full
          flex items-center justify-center
          text-sm font-bold
          ${isDark 
            ? "bg-green-500 text-black" 
            : "bg-green-900 text-white"
          }
        `}>
          {index + 1}
        </div>
        
        {/* Content */}
        <div className="ml-12 space-y-3">
          <h3 className={`text-xl md:text-2xl font-bold ${
            isDark ? "text-green-400" : "text-green-900"
          }`}>
            {step.title}
          </h3>
          <p className={`text-base md:text-lg leading-relaxed ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}>
          {step.description}
        </p>
      </div>
    </div>
    </animated.article>
  );
};

export default function AgencyProcessFlow() {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleCTAClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    const formUrl = language === "en" 
      ? 'https://tally.so/r/wvJ2jl'
      : 'https://tally.so/r/nPb4oQ';
    const win = window.open(formUrl, 'DynovantContactForm', 'noopener,noreferrer');
    if (win) win.opener = null;
    setIsModalOpen(false);
  };

  const steps = [
    {
      title: language === "en" ? "Initial Contact" : "Contact Initial",
        description:
          language === "en"
          ? "Schedule a discovery call to discuss your business challenges, objectives, and potential automation opportunities. We'll outline how our expertise aligns with your goals."
          : "Planifiez un appel de découverte pour discuter de vos défis commerciaux, objectifs et opportunités d'automatisation. Nous expliquerons comment notre expertise s'aligne sur vos objectifs.",
        icon: faHandshake,
      },
      {
        title: language === "en" ? "Needs Assessment" : "Évaluation des Besoins",
        description:
          language === "en"
          ? "Our experts conduct a thorough analysis of your current processes, systems, and pain points. We identify key areas for improvement and potential automation solutions."
          : "Nos experts effectuent une analyse approfondie de vos processus actuels, systèmes et points problématiques. Nous identifions les domaines clés d'amélioration et les solutions d'automatisation potentielles.",
        icon: faSearch,
      },
      {
      title: language === "en" ? "Automation Analysis" : "Analyse d'Automatisation",
        description:
          language === "en"
          ? "We evaluate technical feasibility, ROI potential, and integration requirements. Our team creates a comprehensive automation strategy tailored to your specific needs."
          : "Nous évaluons la faisabilité technique, le potentiel de ROI et les besoins d'intégration. Notre équipe crée une stratégie d'automatisation complète adaptée à vos besoins spécifiques.",
        icon: faCogs,
      },
      {
      title: language === "en" ? "Proposal" : "Proposition",
        description:
          language === "en"
          ? "Receive a detailed project proposal including scope, timeline, deliverables, and expected outcomes. We outline the implementation approach and resource requirements."
          : "Recevez une proposition détaillée incluant la portée, le calendrier, les livrables et les résultats attendus. Nous décrivons l'approche de mise en œuvre et les ressources nécessaires.",
        icon: faFileAlt,
      },
      {
        title: language === "en" ? "Development" : "Développement",
        description:
          language === "en"
          ? "Our development team builds your custom solution using industry best practices. We maintain regular communication and provide progress updates throughout the development phase."
          : "Notre équipe de développement construit votre solution sur mesure en utilisant les meilleures pratiques du secteur. Nous maintenons une communication régulière et fournissons des mises à jour sur l'avancement.",
        icon: faLaptopCode,
      },
      {
        title: language === "en" ? "Testing" : "Tests",
        description:
        language === "en" 
          ? "Rigorous testing in a staging environment ensures reliability and performance. We validate functionality, security, and integration with your existing systems."
          : "Des tests rigoureux dans un environnement de préproduction garantissent la fiabilité et la performance. Nous validons la fonctionnalité, la sécurité et l'intégration avec vos systèmes existants.",
        icon: faVial,
      },
      {
        title: language === "en" ? "Training" : "Formation Client",
        description:
          language === "en"
          ? "Comprehensive training sessions for your team ensure smooth adoption. We provide documentation, best practices, and hands-on guidance for optimal system utilization."
          : "Des sessions de formation complètes pour votre équipe assurent une adoption en douceur. Nous fournissons la documentation, les meilleures pratiques et des conseils pratiques pour une utilisation optimale.",
        icon: faGraduationCap,
      },
      {
        title: language === "en" ? "Support" : "Support",
        description:
        language === "en" 
          ? "Ongoing technical support and maintenance ensure your solution continues to perform optimally. We provide regular updates and remain available for any assistance needed."
          : "Le support technique et la maintenance continue assurent que votre solution continue de fonctionner de manière optimale. Nous fournissons des mises à jour régulières et restons disponibles pour toute assistance.",
        icon: faLifeRing,
      },
  ];

  return (
    <>
      <section
        id="process"
        className="relative py-32 px-8 sm:px-12 lg:px-16 overflow-hidden"
        ref={ref}
        aria-label="Our Process"
      >
        <div className="max-w-[1600px] mx-auto">
          {/* Title */}
          <header className="text-center mb-32">
            <h1 className={`
              text-4xl md:text-5xl lg:text-6xl 
          font-bold 
              ${isDark 
            ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
            : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
          }
          bg-clip-text 
          text-transparent
              mb-6
          animate-gradient-x
        `}>
            {language === "en" ? "Our Process" : "Notre Processus"}
            </h1>
            <div className={`w-24 h-1 mx-auto rounded-full mt-6 ${
              isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50"
                : "bg-gradient-to-r from-green-900/50 to-green-700/50"
            }`} />
          </header>

          {/* Timeline */}
          <main className="max-w-3xl mx-auto space-y-12">
            {steps.map((step, index) => (
              <ProcessStep
                key={index}
                step={step}
                index={index}
                totalSteps={steps.length}
                inView={inView}
                isDark={isDark}
                isLast={index === steps.length - 1}
              />
            ))}
          </main>

          {/* CTA Section */}
          <footer className="mt-24 text-center">
            <h3 className={`text-3xl md:text-4xl font-bold mb-6 ${
              isDark ? "text-green-400" : "text-green-900"
            }`}>
              {language === "en" 
                ? "Ready to Start Your Digital Transformation?" 
                : "Prêt à Démarrer Votre Transformation Digitale ?"}
            </h3>
            <p className={`text-xl mb-8 max-w-2xl mx-auto ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}>
              {language === "en"
                ? "Take the first step towards optimizing your business operations."
                : "Faites le premier pas vers l'optimisation de vos opérations."}
            </p>
            <button
              onClick={handleCTAClick}
              className={`
                px-8 py-4
                rounded-lg
                text-xl
                font-semibold
                transition-all
                duration-300
                ${isDark
                  ? "bg-green-500 text-black hover:bg-green-400"
                  : "bg-green-600 text-white hover:bg-green-500"
                }
              `}
            >
              {language === "en" ? "Begin Your Journey" : "Commencez Votre Parcours"}
            </button>
          </footer>

        </div>

        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
            isDark ? "bg-green-500/5" : "bg-green-900/5"
          }`} />
          <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
            isDark ? "bg-green-500/5" : "bg-green-900/5"
          }`} />
      </div>
      </section>

      <PrivacyConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
}
